import React, { useState, useEffect } from 'react';
import { Trash2, Upload } from 'lucide-react';
import { frontendDb as db } from '../../lib/frontend-db';
import type { Font } from '../../lib/frontend-db';

const SettingsPage = () => {
  const [primaryColor, setPrimaryColor] = useState('#a2bba3');
  const [hexInput, setHexInput] = useState('#a2bba3');
  const [fontFamily, setFontFamily] = useState('system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif');
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [currentLogo, setCurrentLogo] = useState('');
  const [logoAlt, setLogoAlt] = useState('StimmArt Logo');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [headerLogo, setHeaderLogo] = useState<File | null>(null);
  const [headerLogoPreview, setHeaderLogoPreview] = useState('');
  const [currentHeaderLogo, setCurrentHeaderLogo] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [aboutTitle, setAboutTitle] = useState('Das sind wir!');
  const [galleryTitle, setGalleryTitle] = useState('Galerie');
  const [newsTitle, setNewsTitle] = useState('Aktuelles');
  const [postersTitle, setPostersTitle] = useState('Aktuelles Konzert');
  const [vereineTitle, setVereineTitle] = useState('Unsere Vereine');
  const [availableFonts, setAvailableFonts] = useState<Font[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
    
    // Listen for font upload events to refresh available fonts
    const handleFontUpload = () => {
      console.log('🔄 Font upload detected in settings, refreshing...');
      setTimeout(loadSettings, 1000);
    };

    window.addEventListener('fontUploaded', handleFontUpload);
    return () => window.removeEventListener('fontUploaded', handleFontUpload);
  }, []);

  const loadSettings = async () => {
    try {
      const [settings, fonts] = await Promise.all([
        db.getSettings(),
        db.getFonts()
      ]);
      
      if (settings) {
        setPrimaryColor(settings.primary_color);
        setHexInput(settings.primary_color);
        setFontFamily(settings.font_family || 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif');
        setCurrentLogo(settings.logo_url || '');
        setLogoAlt(settings.logo_alt || 'StimmArt Logo');
        setFacebookUrl(settings.facebook_url || '');
        setInstagramUrl(settings.instagram_url || '');
        setCurrentHeaderLogo((settings as any).header_logo_url || '');
        setYoutubeUrl((settings as any).about_youtube_url || '');
        setAboutTitle((settings as any).about_title || 'Das sind wir!');
        setGalleryTitle((settings as any).gallery_title || 'Galerie');
        setNewsTitle((settings as any).news_title || 'Aktuelles');
        setPostersTitle((settings as any).posters_title || 'Aktuelles Konzert');
        setVereineTitle((settings as any).vereine_title || 'Unsere Vereine');
        
        // Apply settings immediately
        applySettings({
          primary_color: settings.primary_color,
          font_family: settings.font_family || 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        });
      }
      
      setAvailableFonts(fonts);
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const applySettings = (settings: { primary_color?: string; font_family?: string }) => {
    if (settings.primary_color) {
      document.documentElement.style.setProperty('--primary-color', settings.primary_color);
    }
    if (settings.font_family) {
      // Apply font family with proper fallbacks
      const fontFamilyWithFallbacks = `"${settings.font_family}", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`;
      document.documentElement.style.setProperty('--font-family', fontFamilyWithFallbacks);
      document.body.style.fontFamily = fontFamilyWithFallbacks;
      
      console.log('Applied font family:', fontFamilyWithFallbacks);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleHeaderLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setHeaderLogo(file);
      setHeaderLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleDeleteHeaderLogo = () => {
    setHeaderLogo(null);
    setHeaderLogoPreview('');
    setCurrentHeaderLogo('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      let logoUrl = currentLogo;
      let headerLogoUrl = currentHeaderLogo;

      if (logo) {
        try {
          console.log('📤 Starting logo upload');
          logoUrl = await db.storeImage(logo);
          console.log('✅ Logo uploaded successfully:', logoUrl);
        } catch (logoError) {
          console.error('❌ Error uploading logo:', logoError);
          setError('Fehler beim Hochladen des Logos: ' + (logoError as Error).message);
          return;
        }
      }

      if (headerLogo) {
        try {
          console.log('📤 Starting header logo upload');
          headerLogoUrl = await db.storeImage(headerLogo);
          console.log('✅ Header logo uploaded successfully:', headerLogoUrl);
        } catch (headerLogoError) {
          console.error('❌ Error uploading header logo:', headerLogoError);
          setError('Fehler beim Hochladen des Header-Logos: ' + (headerLogoError as Error).message);
          return;
        }
      }

      try {
        console.log('💾 Saving settings to database');
        await db.updateSettings({
          primary_color: primaryColor,
          font_family: fontFamily,
          logo_url: logoUrl,
          logo_alt: logoAlt,
          facebook_url: facebookUrl,
          instagram_url: instagramUrl,
          header_logo_url: headerLogoUrl,
          about_youtube_url: youtubeUrl,
          about_title: aboutTitle,
          gallery_title: galleryTitle,
          news_title: newsTitle,
          posters_title: postersTitle,
          vereine_title: vereineTitle
        });
        console.log('✅ Settings saved successfully');
      } catch (settingsError) {
        console.error('❌ Error saving settings:', settingsError);
        setError('Fehler beim Speichern der Einstellungen: ' + (settingsError as Error).message);
        return;
      }

      // Apply the settings immediately
      applySettings({
        primary_color: primaryColor,
        font_family: fontFamily
      });
      
      setCurrentLogo(logoUrl);
      setLogo(null);
      setLogoPreview('');
      setSuccess(true);
      
      // Trigger event to update components immediately
      window.dispatchEvent(new CustomEvent('settingsUpdated'));
      
      // Also trigger a second event after a short delay to ensure database is updated
      setTimeout(() => {
        console.log('🔄 Settings: Triggering delayed settings update event');
        window.dispatchEvent(new CustomEvent('settingsUpdated'));
      }, 200);
      
      // Small delay to ensure updates complete
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('❌ Unexpected error saving settings:', error);
      setError('Unerwarteter Fehler beim Speichern der Einstellungen: ' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (value: string) => {
    setPrimaryColor(value);
    setHexInput(value);
    applySettings({ primary_color: value });
  };

  const handleHexInput = (value: string) => {
    setHexInput(value);
    if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
      setPrimaryColor(value);
      applySettings({ primary_color: value });
    }
  };

  const handleFontChange = (newFont: string) => {
    setFontFamily(newFont);
    
    if (newFont === 'System Default') {
      // Remove custom font styling
      document.documentElement.style.removeProperty('--font-family');
      document.body.style.fontFamily = '';
      
      // Remove global font style
      const existingStyle = document.getElementById('global-font-style');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      console.log('🔄 Applied system default font');
    } else {
      // Apply custom font immediately
      const fontFamilyWithFallbacks = `"${newFont}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      
      // Set CSS variables and body font
      document.documentElement.style.setProperty('--font-family', fontFamilyWithFallbacks);
      document.body.style.fontFamily = fontFamilyWithFallbacks;
      
      // Add global CSS to ensure font is applied everywhere
      const fontStyle = document.createElement('style');
      fontStyle.id = 'global-font-style';
      fontStyle.textContent = `
        *, *::before, *::after {
          font-family: ${fontFamilyWithFallbacks} !important;
        }
      `;
      
      // Remove existing global font style if present
      const existingStyle = document.getElementById('global-font-style');
      if (existingStyle) {
        existingStyle.remove();
      }
      
      document.head.appendChild(fontStyle);
      console.log('✅ Applied global font family:', fontFamilyWithFallbacks);
      
      // Force font loading and test
      if ('fonts' in document) {
        document.fonts.load(`16px "${newFont}"`).then(() => {
          console.log(`✅ Font "${newFont}" applied in settings`);
          
          // Test if font actually loaded
          setTimeout(() => {
            const testElement = document.createElement('div');
            testElement.style.fontFamily = `"${newFont}", monospace`;
            testElement.style.position = 'absolute';
            testElement.style.visibility = 'hidden';
            testElement.style.fontSize = '20px';
            testElement.textContent = 'Test';
            
            document.body.appendChild(testElement);
            const width1 = testElement.offsetWidth;
            
            testElement.style.fontFamily = 'monospace';
            const width2 = testElement.offsetWidth;
            
            document.body.removeChild(testElement);
            
            const isLoaded = width1 !== width2;
            console.log(`🎯 Font "${newFont}" test result: ${isLoaded ? 'LOADED' : 'NOT LOADED'}`);
          }, 300);
        }).catch(error => {
          console.error(`❌ Error loading font "${newFont}":`, error);
        });
      }
    }
  };

  const removeLogo = () => {
    setCurrentLogo('');
    setLogo(null);
    setLogoPreview('');
  };

  if (loading) {
    return <div className="animate-pulse">Lädt...</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 mb-4">
          Einstellungen wurden erfolgreich gespeichert
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Einstellungen</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Primärfarbe</label>
            <div className="flex gap-4 items-start">
              <div className="flex-1">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="block w-full h-10"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">HEX Code</label>
                <input
                  type="text"
                  value={hexInput}
                  onChange={(e) => handleHexInput(e.target.value)}
                  pattern="^#[0-9A-Fa-f]{6}$"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  placeholder="#000000"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Schriftart</label>
            <select
              value={fontFamily}
              onChange={(e) => handleFontChange(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
            >
              <option value="System Default">System Default</option>
              {availableFonts.map((font) => (
                <option key={font.id} value={font.name}>
                  {font.name}
                </option>
              ))}
            </select>
            {availableFonts.length > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                {availableFonts.length} benutzerdefinierte Schriftart{availableFonts.length !== 1 ? 'en' : ''} verfügbar
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="mt-1 block w-full"
            />
            
            {(logoPreview || currentLogo) && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Aktuelles Logo:</span>
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Logo entfernen
                  </button>
                </div>
                <div className="border rounded-lg p-4 bg-gray-50 flex justify-center">
                  <img
                    src={logoPreview || currentLogo}
                    alt="Logo Vorschau"
                    className="max-h-32 w-auto object-contain"
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Logo Alt-Text</label>
            <input
              type="text"
              value={logoAlt}
              onChange={(e) => setLogoAlt(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
              placeholder="Beschreibung für das Logo (für Barrierefreiheit)"
            />
          </div>

          {/* Social Media Links */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Social Media Links</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span>Facebook URL</span>
                  </div>
                </label>
                <input
                  type="url"
                  value={facebookUrl}
                  onChange={(e) => setFacebookUrl(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  placeholder="https://www.facebook.com/ihre-seite"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Die Facebook-URL wird als anklickbares Logo in der "Über uns" Sektion angezeigt
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    <span>Instagram URL</span>
                  </div>
                </label>
                <input
                  type="url"
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
                  placeholder="https://www.instagram.com/ihr-account"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Die Instagram-URL wird als anklickbares Logo in der "Über uns" Sektion angezeigt
                </p>
              </div>
            </div>
          </div>

          {/* Header Logo */}
          <div className="mt-6 p-6 bg-yellow-50 rounded-lg border border-yellow-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Header-Logo & Favicon</h3>
            
            {(currentHeaderLogo || headerLogoPreview) && (
              <div className="mb-4 flex flex-col items-center p-4 bg-white rounded-lg">
                <img 
                  src={headerLogoPreview || currentHeaderLogo} 
                  alt="Header Logo Preview" 
                  className="max-h-[60px] w-auto object-contain mb-3"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleDeleteHeaderLogo}
                    className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm"
                  >
                    <Trash2 size={16} />
                    Logo löschen
                  </button>
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Logo für Header hochladen
              </label>
              <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                <Upload size={20} className="mr-2" />
                {currentHeaderLogo || headerLogoPreview ? 'Logo ändern' : 'Logo hochladen'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleHeaderLogoChange}
                  className="hidden"
                />
              </label>
              <p className="text-sm text-gray-500 mt-2">
                Das Logo wird oben links im Header angezeigt und automatisch als Favicon (Tab-Symbol) verwendet. Empfohlene Höhe: 60px
              </p>
            </div>
          </div>

          {/* YouTube URL */}
          <div className="mt-6 p-6 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">YouTube-Video ("Das sind wir!")</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube-Video URL
              </label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-200"
                placeholder="https://www.youtube.com/watch?v=..."
              />
              <p className="text-sm text-gray-500 mt-2">
                Das Video wird in der "Das sind wir!" Sektion eingebettet. Unterstützte Formate: youtube.com/watch?v= oder youtu.be/
              </p>
            </div>
          </div>

          {/* Sektions-Überschriften */}
          <div className="mt-6 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sektions-Überschriften</h3>
            <p className="text-sm text-gray-600 mb-4">
              Diese Überschriften werden sowohl auf der Startseite als auch im Admin-Panel angezeigt.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  "Das sind wir!" Sektion
                </label>
                <input
                  type="text"
                  value={aboutTitle}
                  onChange={(e) => setAboutTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  placeholder="Das sind wir!"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bildergalerie
                </label>
                <input
                  type="text"
                  value={galleryTitle}
                  onChange={(e) => setGalleryTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  placeholder="Galerie"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nachrichten
                </label>
                <input
                  type="text"
                  value={newsTitle}
                  onChange={(e) => setNewsTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  placeholder="Aktuelles"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Veranstaltungsplakate
                </label>
                <input
                  type="text"
                  value={postersTitle}
                  onChange={(e) => setPostersTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  placeholder="Aktuelles Konzert"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vereine
                </label>
                <input
                  type="text"
                  value={vereineTitle}
                  onChange={(e) => setVereineTitle(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                  placeholder="Unsere Vereine"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 mt-6"
          >
            {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SettingsPage;

